/**
 * Stage 10: Confirmed Published — context-based version using StagePanel.
 *
 * Not an LLM stage. Marks the article as completed and removes it from
 * the active pipeline. Uses a custom action instead of executeStage.
 */

import React, { useCallback, useState } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { blogCandidatesService } from '../../../services/blogCandidatesService';
import { blogRunsService } from '../../../services/blogRunsService';
import type { ArticleDocument } from '../types';

function renderOutput(doc: ArticleDocument) {
  const wp = doc.preflight?.wordpress;
  const queries = doc.preflight?.ai_queries;
  return (
    <div className="space-y-2 text-xs text-gray-600">
      <div>
        WordPress: {wp ? `Post #${wp.post_id} — ${wp.post_status}` : 'Not published'}
      </div>
      <div>
        AI Queries: {queries ? `${queries.length} queries` : 'Not generated'}
      </div>
    </div>
  );
}

export function Stage10ConfirmedPublishedV2() {
  const { candidate, run, refreshDocument, refreshArticles } = useArticleContext();
  const [confirming, setConfirming] = useState(false);

  const isAlreadyConfirmed = Boolean(candidate?.published_confirmed_at);

  const handleConfirm = useCallback(async () => {
    if (!candidate || !run) return;
    setConfirming(true);
    try {
      await blogCandidatesService.update(candidate.candidate_id, {
        published_confirmed_at: new Date().toISOString(),
      });
      await blogRunsService.update(run.run_id, {
        status: 'succeeded',
      });
      await refreshDocument();
      await refreshArticles();
    } catch (e: any) {
      // Error will show in console; could add error state if needed
      console.error('Failed to confirm publication:', e);
    } finally {
      setConfirming(false);
    }
  }, [candidate, run, refreshDocument, refreshArticles]);

  return (
    <StagePanel
      stageId="stage_10_confirmed_published"
      title="Stage 10: Confirm Published"
      description="Marks the article as completed and removes it from the active pipeline."
      hideRunButton
      renderOutput={renderOutput}
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Click this button when the article has been published from WordPress and all
          post-publication work is complete. This marks the article as <strong>Published</strong>{' '}
          and removes it from the pipeline.
        </p>
        <button
          type="button"
          className="px-4 py-2 text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#1a2332' }}
          disabled={confirming || isAlreadyConfirmed}
          onClick={handleConfirm}
        >
          {confirming ? 'Working...' : isAlreadyConfirmed ? 'Already Confirmed' : 'Confirm Published'}
        </button>
        {isAlreadyConfirmed && (
          <div className="text-xs text-green-600">
            Confirmed at: {candidate?.published_confirmed_at}
          </div>
        )}
      </div>
    </StagePanel>
  );
}
